import { Fragment } from "react"

import { DeltaCell } from "~/components/demand-2/shared/demandBreakdownShared"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { EffectiveDemand } from "~/utils/demandEffective"
import { INSIGHT_PREFIX, type InsightScalars } from "~/utils/demandInsightValues"
import { formatNumber } from "~/utils/formatting"
import { cn } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"

type Landing = { before: number; change: number }

type GrowthLanding = {
  /** Not pre-orders, plus the pre-orders still expected: all growth may consume. */
  unconfirmed: Landing
  confirmed: Landing
}

/**
 * The engine's applied growth split by what it can move: all of it lands on unconfirmed demand,
 * so the change sums to the Demand signals' growth. Null until the offers read carries the parts.
 */
export const growthLandingOf = (
  variants: readonly IVariantSummary[],
  scalarsFor: (variantId: string) => InsightScalars | null,
  effective: (v: IVariantSummary) => Pick<EffectiveDemand, "setupAttribution">,
): GrowthLanding | null => {
  const out: GrowthLanding = {
    unconfirmed: { before: 0, change: 0 },
    confirmed: { before: 0, change: 0 },
  }
  let seen = false
  for (const v of variants) {
    const s = scalarsFor(v.variantId)
    if (s?.seasonTrendBase == null) continue
    seen = true
    out.unconfirmed.before += s.seasonTrendBase
    out.unconfirmed.change += effective(v).setupAttribution[`${INSIGHT_PREFIX}seasonTrend`] ?? 0
    out.confirmed.before += s.confirmedPreorders ?? 0
  }
  return seen ? out : null
}

const unitsCell = (n: number) => formatNumber(Math.round(n))

/** Laid out as the Cannibalization card's "Change in sales" table. */
const GrowthLandingTable = ({ landing }: { landing: GrowthLanding }) => {
  const preorders = formatName("preorder", true).toLowerCase()
  const rows = [
    { label: `Confirmed ${preorders}`, landing: landing.confirmed },
    { label: "Unconfirmed demand", landing: landing.unconfirmed },
  ]
  const total: Landing = {
    before: rows.reduce((sum, r) => sum + r.landing.before, 0),
    change: rows.reduce((sum, r) => sum + r.landing.change, 0),
  }
  const cell = "text-right tabular-nums"
  const totalCell = "border-t border-base pt-1"
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,auto)] gap-x-6 gap-y-1 border-t border-base pt-3 paragraph-sm">
      <span className="label-sm text-fg-base">Where the growth lands</span>
      <span className={cn(cell, "paragraph-xs text-fg-subtle")}>Before growth</span>
      <span className={cn(cell, "paragraph-xs text-fg-subtle")}>After growth</span>
      <span className={cn(cell, "paragraph-xs text-fg-subtle")}>Change</span>
      {rows.map(({ label, landing: l }) => (
        <Fragment key={label}>
          <span className="text-fg-subtle">{label}</span>
          <span className={cn(cell, "text-fg-subtle")}>{unitsCell(l.before)}</span>
          <span className={cn(cell, "text-fg-subtle")}>{unitsCell(l.before + l.change)}</span>
          <span className="flex justify-end">
            <DeltaCell value={l.change} />
          </span>
        </Fragment>
      ))}
      <span className={cn("label-sm text-fg-base", totalCell)}>Total</span>
      <span className={cn(cell, totalCell, "text-fg-base")}>{unitsCell(total.before)}</span>
      <span className={cn(cell, totalCell, "text-fg-base")}>
        {unitsCell(total.before + total.change)}
      </span>
      <span className={cn("flex justify-end", totalCell)}>
        <DeltaCell value={total.change} />
      </span>
      <span className="col-span-4 paragraph-xs text-fg-subtle">
        {`Growth never moves confirmed ${preorders}.`}
      </span>
    </div>
  )
}

export default GrowthLandingTable
